import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Checkbox, DatePicker, Flex, Form, Input, InputNumber, Modal, Radio, Select, Space, Switch, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useDeferredValue, useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { SkuSelect } from "../catalog/mapping";
import { PLATFORMS, useConnections } from "../channels/hooks";

type Promo = Schemas["Promotion"];
type Values = {
  name: string;
  active: boolean;
  priority: number;
  exclusive: boolean;
  period?: [Dayjs | null, Dayjs | null] | null;
  connectionIds?: string[];
  platforms?: Schemas["PromoConditions"]["platforms"];
  minTotal?: number | null;
  requiredSkuIds?: string[];
  buySkuIds?: string[];
  buyMinQty?: number | null;
  multiply?: boolean;
  perQty?: number | null;
  giftSkuId?: string;
  giftQty: number;
  maxPerOrder?: number | null;
  quota?: number | null;
  whenOut: Promo["whenOut"];
};

/**
 * Promosi (Plan §6.13): the seller's gift rules, applied once when an order is
 * processed. The gift is added to the order at Rp0, reserved, picked and packed.
 */
export function Promotions() {
  const { t, formatDateTime, formatMoney, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");
  const [editing, setEditing] = useState<Promo | "new" | null>(null);
  const promos = useQuery({ queryKey: ["promotions"], queryFn: () => unwrap(api.GET("/v1/promotions")) });
  const skuCodes = useSkuCodes();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["promotions"] });
  const toggle = useMutation({
    mutationFn: (p: Promo) =>
      unwrap(
        api.PATCH("/v1/promotions/{promoID}", {
          params: { path: { promoID: p.id } },
          body: {
            name: p.name, active: !p.active, priority: p.priority, exclusive: p.exclusive, startsAt: p.startsAt, endsAt: p.endsAt,
            conditions: p.conditions, giftSkuId: p.giftSkuId, giftQty: p.giftQty, perQty: p.perQty, maxPerOrder: p.maxPerOrder,
            quota: p.quota, whenOut: p.whenOut,
          },
        }),
      ),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (p: Promo) => unwrap(api.DELETE("/v1/promotions/{promoID}", { params: { path: { promoID: p.id } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });

  const conditionText = (p: Promo) => {
    const c = p.conditions;
    const parts: string[] = [];
    if (c.buySkuIds?.length) parts.push(t("promo.buyText", { n: c.buyMinQty ?? 1, skus: c.buySkuIds.map(skuCodes).join(" / ") }));
    if (c.minTotal != null) parts.push(t("promo.spendText", { amount: formatMoney(c.minTotal) }));
    if (c.requiredSkus?.length) parts.push(t("promo.withText", { skus: c.requiredSkus.map((r) => skuCodes(r.skuId)).join(", ") }));
    if (c.platforms?.length) parts.push(c.platforms.map((x) => t(`platform.${x}`)).join(", "));
    if (c.connectionIds?.length) parts.push(t("promo.storesText", { n: c.connectionIds.length }));
    return parts.join(" · ");
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 8 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("promo.title")}
        </Typography.Title>
        {manage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("promo.add")}
          </Button>
        )}
      </Flex>
      <Typography.Paragraph type="secondary">{t("promo.help")}</Typography.Paragraph>
      <Table<Promo>
        rowKey="id"
        loading={promos.isPending}
        dataSource={promos.data?.items ?? []}
        pagination={CLIENT_PAGING}
        locale={{ emptyText: t("promo.empty") }}
        columns={[
          {
            title: t("common.name"),
            dataIndex: "name",
            render: (v: string, p) => (
              <Space orientation="vertical" size={0}>
                <Space size={6}>
                  <Typography.Text strong>{v}</Typography.Text>
                  {p.exclusive && <Tag>{t("promo.exclusive")}</Tag>}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {conditionText(p)}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("promo.gift"),
            width: 230,
            render: (_, p) => (
              <Space orientation="vertical" size={0}>
                <span>
                  <span className="z-num">{p.giftQty}×</span> {p.giftCode}
                  {p.perQty ? <Typography.Text type="secondary"> {t("promo.perText", { n: p.perQty })}</Typography.Text> : null}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`promo.whenOut.${p.whenOut}`)}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("promo.period"),
            width: 220,
            render: (_, p) =>
              p.startsAt || p.endsAt ? `${p.startsAt ? formatDateTime(p.startsAt, tz) : "…"} – ${p.endsAt ? formatDateTime(p.endsAt, tz) : "…"}` : t("promo.always"),
          },
          {
            title: t("promo.used"),
            width: 120,
            align: "right",
            render: (_, p) => <span className="z-num">{p.quota != null ? `${formatNumber(p.used)} / ${formatNumber(p.quota)}` : formatNumber(p.used)}</span>,
          },
          {
            title: t("orders.rule.active"),
            width: 90,
            render: (_, p) => <Switch checked={p.active} disabled={!manage} loading={toggle.isPending && toggle.variables?.id === p.id} onChange={() => toggle.mutate(p)} />,
          },
          ...(manage
            ? [
                {
                  title: "",
                  width: 150,
                  render: (_: unknown, p: Promo) => (
                    <Space>
                      <Button size="small" onClick={() => setEditing(p)}>
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          modal.confirm({
                            title: t("promo.delete"),
                            content: t("promo.deleteConfirm", { name: p.name }),
                            okButtonProps: { danger: true },
                            okText: t("common.delete"),
                            cancelText: t("common.cancel"),
                            onOk: () => remove.mutateAsync(p),
                          })
                        }
                      >
                        {t("common.delete")}
                      </Button>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      <PromoModal promo={editing} onClose={() => setEditing(null)} onSaved={refresh} />
    </>
  );
}

/** Codes of the SKUs promotions name, for their summaries. */
function useSkuCodes() {
  const skus = useQuery({ queryKey: ["skus", "all-codes"], queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { limit: 200 } } })) });
  return (id: string) => skus.data?.items.find((s) => s.id === id)?.code ?? "SKU";
}

function SkuMulti({ value, onChange }: { value?: string[]; onChange?: (v: string[]) => void }) {
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const list = useQuery({
    queryKey: ["skus", "pick", "multi", q],
    queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { status: "active", kind: "single", limit: 50, ...(q ? { q } : {}) } } })),
  });
  const known = useSkuCodes();
  const options = (list.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }));
  for (const id of value ?? []) {
    if (!options.some((o) => o.value === id)) options.push({ value: id, label: known(id) });
  }
  return (
    <Select<string[]>
      mode="multiple"
      allowClear
      value={value}
      onChange={onChange}
      showSearch={{ filterOption: false, onSearch: setSearch }}
      loading={list.isFetching}
      options={options}
    />
  );
}

function PromoModal({ promo, onClose, onSaved }: { promo: Promo | "new" | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<Values>();
  const multiply = Form.useWatch("multiply", form);
  const buy = Form.useWatch("buySkuIds", form);
  const connections = useConnections();
  const existing = promo && promo !== "new" ? promo : null;
  const c = existing?.conditions ?? {};
  const initial: Values = existing
    ? {
        name: existing.name, active: existing.active, priority: existing.priority, exclusive: existing.exclusive,
        period: existing.startsAt || existing.endsAt ? [existing.startsAt ? dayjs(existing.startsAt) : null, existing.endsAt ? dayjs(existing.endsAt) : null] : null,
        connectionIds: c.connectionIds, platforms: c.platforms, minTotal: c.minTotal, requiredSkuIds: c.requiredSkus?.map((r) => r.skuId),
        buySkuIds: c.buySkuIds, buyMinQty: c.buyMinQty, multiply: existing.perQty != null, perQty: existing.perQty,
        giftSkuId: existing.giftSkuId, giftQty: existing.giftQty, maxPerOrder: existing.maxPerOrder, quota: existing.quota, whenOut: existing.whenOut,
      }
    : { name: "", active: true, priority: 100, exclusive: false, giftQty: 1, whenOut: "skip" };
  const save = useMutation({
    mutationFn: (v: Values) => {
      const conditions: Schemas["PromoConditions"] = {
        ...(v.connectionIds?.length ? { connectionIds: v.connectionIds } : {}),
        ...(v.platforms?.length ? { platforms: v.platforms } : {}),
        ...(v.minTotal != null ? { minTotal: v.minTotal } : {}),
        ...(v.requiredSkuIds?.length ? { requiredSkus: v.requiredSkuIds.map((skuId) => ({ skuId, minQty: 1 })) } : {}),
        ...(v.buySkuIds?.length ? { buySkuIds: v.buySkuIds } : {}),
        ...(v.buySkuIds?.length && v.buyMinQty != null ? { buyMinQty: v.buyMinQty } : {}),
      };
      const body = {
        name: v.name, active: v.active, priority: v.priority, exclusive: v.exclusive,
        startsAt: v.period?.[0] ? v.period[0].toISOString() : null, endsAt: v.period?.[1] ? v.period[1].toISOString() : null,
        conditions, giftSkuId: v.giftSkuId!, giftQty: v.giftQty, perQty: v.multiply && v.buySkuIds?.length ? (v.perQty ?? 1) : null,
        maxPerOrder: v.maxPerOrder ?? null, quota: v.quota ?? null, whenOut: v.whenOut,
      };
      return existing
        ? unwrap(api.PATCH("/v1/promotions/{promoID}", { params: { path: { promoID: existing.id } }, body }))
        : unwrap(api.POST("/v1/promotions", { body }));
    },
    onSuccess: () => {
      void message.success(t("promo.saved"));
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={promo !== null}
      title={existing ? t("promo.edit") : t("promo.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      width={720}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" initialValues={initial} onFinish={(v) => save.mutate(v)} requiredMark={false}>
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, whitespace: true, max: 100 }]}>
          <Input />
        </Form.Item>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("promo.examplesTitle")} description={t("promo.examples")} />
        <Typography.Text strong>{t("orders.rule.conditions")}</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t("promo.conditionsHelp")}
        </Typography.Paragraph>
        <Space wrap align="start">
          <Form.Item name="buySkuIds" label={t("promo.buySkus")} style={{ width: 400 }}>
            <SkuMulti />
          </Form.Item>
          <Form.Item name="buyMinQty" label={t("promo.buyMinQty")}>
            <InputNumber min={1} precision={0} style={{ width: 140 }} disabled={!buy?.length} />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="minTotal" label={t("promo.minTotal")}>
            <InputNumber min={0} precision={0} prefix="Rp" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="requiredSkuIds" label={t("promo.requiredSkus")} style={{ width: 340 }}>
            <SkuMulti />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="connectionIds" label={t("orders.rule.stores")} style={{ width: 330 }}>
            <Select mode="multiple" allowClear options={(connections.data?.items ?? []).map((x) => ({ value: x.id, label: x.name }))} />
          </Form.Item>
          <Form.Item name="platforms" label={t("orders.rule.platforms")} style={{ width: 300 }}>
            <Select mode="multiple" allowClear options={PLATFORMS.map((p) => ({ value: p, label: t(`platform.${p}`) }))} />
          </Form.Item>
        </Space>
        <Typography.Text strong>{t("promo.gift")}</Typography.Text>
        <Space wrap align="start" style={{ marginTop: 8 }}>
          <Form.Item name="giftSkuId" label={t("promo.giftSku")} rules={[{ required: true, message: t("field.required") }]} style={{ width: 360 }}>
            <SkuSelect />
          </Form.Item>
          <Form.Item name="giftQty" label={t("promo.giftQty")}>
            <InputNumber min={1} max={1000} precision={0} style={{ width: 120 }} />
          </Form.Item>
        </Space>
        <Space wrap align="center">
          <Form.Item name="multiply" valuePropName="checked">
            <Checkbox disabled={!buy?.length}>{t("promo.multiply")}</Checkbox>
          </Form.Item>
          {multiply && (
            <Form.Item name="perQty" label={t("promo.perQty")}>
              <InputNumber min={1} max={1000} precision={0} style={{ width: 120 }} />
            </Form.Item>
          )}
          <Form.Item name="maxPerOrder" label={t("promo.maxPerOrder")}>
            <InputNumber min={1} precision={0} style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="quota" label={t("promo.quota")}>
            <InputNumber min={1} precision={0} style={{ width: 150 }} />
          </Form.Item>
        </Space>
        <Form.Item name="whenOut" label={t("promo.whenOutLabel")}>
          <Radio.Group
            options={[
              { value: "skip", label: t("promo.whenOut.skip") },
              { value: "hold", label: t("promo.whenOut.hold") },
            ]}
          />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="period" label={t("promo.period")}>
            <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" allowEmpty={[true, true]} />
          </Form.Item>
          <Form.Item name="priority" label={t("promo.priority")} extra={t("promo.priorityHelp")}>
            <InputNumber precision={0} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="exclusive" label={t("promo.exclusive")} valuePropName="checked" extra={t("promo.exclusiveHelp")}>
            <Switch />
          </Form.Item>
          <Form.Item name="active" label={t("orders.rule.active")} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}
